import { z } from 'zod';
/**
 * Public Experiences — contact data (the entire MVP1 `CONTACT_FORM_V1` payload).
 *
 * Exactly four fields, all required. Email is contact data, **not** a verified
 * identity: it never authorizes anything, never reuses an existing billing
 * customer, and never produces an "account exists" style response.
 */
/**
 * Names are trimmed then length-checked. There is deliberately **no**
 * alphabet restriction: spaces, hyphens, apostrophes and non-ASCII letters are
 * all valid in real names (e.g. "Mary Jane", "O'Brien", "Al-Rashid",
 * "Nakamura Ayumi", "Renée"). Only length and emptiness are enforced.
 */
export const CONTACT_NAME_MIN_LENGTH = 1;
export const CONTACT_NAME_MAX_LENGTH = 100;
export const CONTACT_EMAIL_MAX_LENGTH = 160;
export const CONTACT_PHONE_MAX_LENGTH = 20;
export const ContactNameSchema = z
    .string()
    .trim()
    .min(CONTACT_NAME_MIN_LENGTH)
    .max(CONTACT_NAME_MAX_LENGTH);
/**
 * Normalization: trim, then lowercase, then RFC-ish email validation, then the
 * length cap. Stored and compared in the normalized form only.
 */
export const ContactEmailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .max(CONTACT_EMAIL_MAX_LENGTH);
/**
 * E.164 phone, matching the output of the frontend's existing
 * `formatPhone(value, { phoneFormat: 'e164' })` helper
 * (`pointcraft-frontend/src/components/number-pad/formatters/phoneFormatter.ts`).
 * The UI collects a US national number and normalizes to `+1XXXXXXXXXX` before
 * sending; the contract itself is country-agnostic E.164.
 */
export const E164_PHONE_REGEX = /^\+[1-9]\d{6,14}$/;
export const ContactPhoneE164Schema = z
    .string()
    .trim()
    .max(CONTACT_PHONE_MAX_LENGTH)
    .regex(E164_PHONE_REGEX, 'phone must be E.164, e.g. +14155550123');
/**
 * US/NANP canonical E.164: `+1` followed by exactly 10 digits, where the area
 * code and the exchange code both start 2-9 (NANP rule), e.g. `+14155550123`.
 */
export const US_E164_PHONE_REGEX = /^\+1[2-9]\d{2}[2-9]\d{6}$/;
/**
 * MVP1 refinement helper: phone input is **US-only** and is persisted as
 * canonical E.164 `+1…`.
 *
 * `ContactPhoneE164Schema` stays general E.164 on purpose, so already-stored
 * numbers keep parsing and supporting another country later needs no schema
 * change — only this narrowing is lifted. Apply it at the input boundary
 * (backend route validation and the frontend form) exactly the way
 * `MVP1_SUPPORTED_INTERVALS` narrows the recurrence.
 */
export const isUsE164 = (phone) => US_E164_PHONE_REGEX.test(phone.trim());
/**
 * The MVP1 input schema for phone: general E.164 **plus** the US-only
 * narrowing. Use this where a guest or admin supplies a phone number; use
 * `ContactPhoneE164Schema` when reading persisted data.
 */
export const ContactPhoneUsE164InputSchema = ContactPhoneE164Schema.refine(isUsE164, { message: 'MVP1 accepts US phone numbers only, as +1 and 10 digits' });
/**
 * The canonical contact payload, as persisted and as returned to an authorized
 * admin. `.strict()` so no extra field (a note, a company, a second email) can be
 * smuggled in and silently persisted. `phone` is general E.164 here so stored
 * data keeps parsing if the supported country set widens.
 */
export const PublicExperienceContactSchema = z
    .object({
    firstName: ContactNameSchema,
    lastName: ContactNameSchema,
    email: ContactEmailSchema,
    phone: ContactPhoneE164Schema,
})
    .strict();
/**
 * The MVP1 **input** contact payload: identical to
 * `PublicExperienceContactSchema` but with the US-only phone narrowing applied.
 * This is what the public checkout request uses.
 */
export const PublicExperienceContactInputSchema = z
    .object({
    firstName: ContactNameSchema,
    lastName: ContactNameSchema,
    email: ContactEmailSchema,
    phone: ContactPhoneUsE164InputSchema,
})
    .strict();
/**
 * Stable field-error keys. The backend returns these verbatim in
 * `details.fieldErrors[].path` so the frontend can attach an error to a control
 * without parsing messages. Never renamed without a CONTRACT_CHANGELOG entry.
 */
export const CONTACT_FIELD_ERROR_KEYS = {
    firstName: 'contact.firstName',
    lastName: 'contact.lastName',
    email: 'contact.email',
    phone: 'contact.phone',
};
export const ContactFieldErrorKeySchema = z.enum([
    CONTACT_FIELD_ERROR_KEYS.firstName,
    CONTACT_FIELD_ERROR_KEYS.lastName,
    CONTACT_FIELD_ERROR_KEYS.email,
    CONTACT_FIELD_ERROR_KEYS.phone,
]);
/**
 * Field-level validation error. `path` is dot-notation and for contact fields is
 * always one of `CONTACT_FIELD_ERROR_KEYS`; `code` is the zod issue code so the
 * frontend can distinguish "too_small" from "invalid_string".
 */
export const PublicExperienceFieldErrorSchema = z
    .object({
    path: z.string().min(1).max(200),
    code: z.string().min(1).max(64),
    message: z.string().min(1).max(500),
})
    .strict();
/**
 * Display-only full name, used for the provider customer record and the admin
 * submission list. Derived, never stored as a separate authoritative field.
 */
export const formatContactFullName = (contact) => `${contact.firstName} ${contact.lastName}`.trim();
